import { useState, useMemo } from 'react';
import { Box, Button, FormControlLabel, Switch } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getTeacherTypesDirect, getSchoolDirect } from '@timetable/api';
import { TeacherList } from './TeacherList';
import { TeacherFormDialog } from './TeacherFormDialog';
import { SearchBar, FilterChips, SortMenu } from '../shared';
import type { FilterOption, SortConfig } from '../shared';
import type { TeacherWithRelations } from '@timetable/types';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuthStore } from '../../store/authStore';

const SORT_OPTIONS = [
  { id: 'name', label: 'Name', field: 'name' },
  { id: 'email', label: 'Email', field: 'email' },
  { id: 'type', label: 'Type', field: 'teacher_type' },
];

export function TeachersTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'name',
    direction: 'asc',
  });
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherWithRelations | null>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const schoolContext = useAuthStore((state) => state.schoolContext);

  const { data: teacherTypes = [] } = useQuery({
    queryKey: ['teacherTypes'],
    queryFn: getTeacherTypesDirect,
  });

  const { data: school } = useQuery({
    queryKey: ['school', schoolContext],
    queryFn: () => getSchoolDirect(schoolContext!),
    enabled: !!schoolContext,
  });

  const schoolName = school?.school_name ?? schoolContext ?? '';

  const teacherTypeFilters = useMemo<FilterOption[]>(() =>
    teacherTypes.map((t) => ({
      id: t.type_name.toLowerCase(),
      label: t.type_name,
      value: t.type_name.toLowerCase(),
    })),
    [teacherTypes]
  );

  const handleAddClick = () => {
    setSelectedTeacher(null);
    setFormOpen(true);
  };

  const handleEditClick = (teacher: TeacherWithRelations) => {
    setSelectedTeacher(teacher);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedTeacher(null);
  };

  return (
    <Box>
      {/* Search and Actions Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search teachers by name, email, or subject..."
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              size="small"
            />
          }
          label="Show inactive"
        />
        <SortMenu
          options={SORT_OPTIONS}
          value={sortConfig}
          onChange={setSortConfig}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add Teacher
        </Button>
      </Box>

      {/* Filter Chips */}
      <Box sx={{ mb: 3 }}>
        <FilterChips
          filters={teacherTypeFilters}
          selectedFilters={selectedFilters}
          onChange={setSelectedFilters}
          label="Filter by type:"
        />
      </Box>

      {/* Teacher List */}
      <TeacherList
        searchQuery={debouncedSearchQuery}
        selectedFilters={selectedFilters}
        sortConfig={sortConfig}
        showInactive={showInactive}
        onEditClick={handleEditClick}
        schoolName={schoolName}
      />

      {/* Form Dialog */}
      <TeacherFormDialog
        open={formOpen}
        teacher={selectedTeacher}
        onClose={handleFormClose}
        schoolName={schoolName}
      />
    </Box>
  );
}
